import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Room } from '../../../shared/types/Room';
import { MoreHorizontalIcon, PlusIcon, EditIcon, ImageIcon, TrashIcon } from 'lucide-react';
export function Rooms() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data based on the screenshot
  const [rooms, setRooms] = useState<Room[]>([{
    id: '1',
    name: 'Scuba Spa Test',
    code: 'Scuba Spa Test',
    category: 'SUPERIOR',
    status: 'inactive',
    image: ''
  }, {
    id: '2',
    name: 'Deluxe Beach Villa',
    code: 'DBV',
    category: 'DELUXE',
    status: 'inactive',
    image: ''
  }, {
    id: '3',
    name: 'Water Villa',
    code: 'SUNSIYAMEX',
    category: 'OVERWATER_VILLA',
    status: 'active',
    image: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/Screenshot_2025-10-01_at_10.57.35_AM.png'
  }, {
    id: '4',
    name: 'Premium Overwater Villa',
    code: 'POWVDTAC',
    category: 'OVERWATER_VILLA',
    status: 'active',
    image: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/Screenshot_2025-10-01_at_10.57.42_AM.png'
  }]);
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  // Action menu state
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  // Filter rooms based on search term and filters
  const filteredRooms = rooms.filter(room => {
    const matchesSearch = room.name.toLowerCase().includes(searchTerm.toLowerCase()) || room.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || statusFilter === 'active' && room.status === 'active' || statusFilter === 'inactive' && room.status === 'inactive';
    const matchesCategory = categoryFilter === 'all' || formatCategoryName(room.category) === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });
  // Format category name for display
  function formatCategoryName(category: string): string {
    return category.replace('_', ' ');
  }
  // Navigate to add room page
  const navigateToAddRoom = () => {
    window.location.href = '/add-room';
  };
  // Navigate to edit room page
  const navigateToEditRoom = (roomId: string) => {
    window.location.href = `/edit-room?id=${roomId}`;
  };
  // Navigate to room assets page
  const navigateToRoomAssets = (roomId: string) => {
    window.location.href = `/room-detail?id=${roomId}`;
  };
  // Delete room
  const deleteRoom = (roomId: string) => {
    if (confirm('Are you sure you want to delete this room?')) {
      setRooms(rooms.filter(room => room.id !== roomId));
    }
  };
  // Toggle action menu
  const toggleActionMenu = (roomId: string) => {
    if (actionMenuOpen === roomId) {
      setActionMenuOpen(null);
    } else {
      setActionMenuOpen(roomId);
    }
  };
  return <div className="space-y-6" data-id="element-10372" data-mp="2jrygn2l9rf54">
      <div className="flex justify-between items-center" data-id="element-10373" data-mp="2esbu22jjwmjn">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-10374" data-mp="2etwozt9s27i1">
          {t('pages.Rooms.rooms')}
        </h1>
        <button onClick={navigateToAddRoom} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center" data-id="element-10375" data-mp="3bht7j3orzcvw">
          <PlusIcon size={18} className="mr-1" data-id="element-10376" data-mp="32phm5w8iqfms" />
          {t('components.AddRoomForm.add_room')}
        </button>
      </div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3" data-id="element-10377" data-mp="2xp3kqx1fa3xq">
        <input type="search" aria-label={t('pages.Rooms.attrs.search_rooms')} placeholder={t('pages.Rooms.attrs.search_rooms')} className="border border-gray-300 rounded-md px-3 py-2 w-48" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-10378" data-mp="3v6tvopntvim7" />

        <select className="border border-gray-300 rounded-md px-3 py-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-10379" data-mp="3f10gvkahda1g">
          <option value="all" data-id="element-10380" data-mp="35zsil27ys97d">{t('components.AddRoomForm.status')}</option>
          <option value="active" data-id="element-10381" data-mp="3tntn8020v5hi">{t('pages.Rates.active')}</option>
          <option value="inactive" data-id="element-10382" data-mp="3tqpkrn9yjs5f">{t('pages.Rates.inactive')}</option>
        </select>
        <select className="border border-gray-300 rounded-md px-3 py-2" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} data-id="element-10383" data-mp="1jrhnk1g2456">
          <option value="all" data-id="element-10384" data-mp="3ow26eh8o4e4k">{t('pages.Rooms.room_category')}</option>
          <option value="SUPERIOR" data-id="element-10385" data-mp="ghjv86nfkigq">{t('pages.Rates.superior')}</option>
          <option value="DELUXE" data-id="element-10386" data-mp="l89xhb0k8577">{t('pages.Rates.deluxe')}</option>
          <option value="OVERWATER VILLA" data-id="element-10387" data-mp="l9uvjam3269t">
            {t('pages.Rates.overwater_villa')}
          </option>
        </select>
      </div>
      {/* Room List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-10388" data-mp="qkp3sq89a9oo">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-10389" data-mp="14dog6jcqrns0">
          <thead className="bg-gray-50" data-id="element-10390" data-mp="14ezlwopqz5lr">
            <tr data-id="element-10391" data-mp="zdsewrvfiag5">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10392" data-mp="zfd8of9x5pcy">
                {t('components.RoomAssets.room_image')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10393" data-mp="7p8oohq2n4c9">
                {t('components.AddRoomForm.room_name')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10394" data-mp="1x0rebadi9whq">
                {t('components.AddRoomForm.room_category')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10395" data-mp="205rsoyv7ob8v">
                {t('components.AddRoomForm.room_code')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10396" data-mp="2e3gfwdcr3uh3">
                {t('components.AddRoomForm.status')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10397" data-mp="1lesri8wpgozb">
                {t('components.RoomAssets.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-10398" data-mp="xo5fpcjo5f7h">
            {filteredRooms.map(room => <tr key={room.id} data-id="element-10399" data-mp="12mgp5m61xnp6">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10400" data-mp="v4ihln7fkmol">
                  {room.image ? <img src={room.image} alt={room.name} className="h-16 w-24 object-cover rounded" data-id="element-10401" data-mp="v25zna1oacbj" /> : <div className="h-16 w-24 bg-gray-100 flex items-center justify-center text-gray-400 text-xs rounded" data-id="element-10402" data-mp="g7hz8ab4q42s">
                      {t('pages.Rooms.no_image')}
                    </div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10403" data-mp="7e61hp217jmx">
                  <div className="text-sm font-medium text-gray-900" data-id="element-10404" data-mp="2flamg8o6cb5">
                    {room.name}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-10405" data-mp="3tn7adxs9pumq">{room.code}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10406" data-mp="3onb5zxfns16n">
                  <div className="text-sm text-gray-900" data-id="element-10407" data-mp="3klg52u1n3pte">
                    {formatCategoryName(room.category)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10408" data-mp="1ce2ultyzvcdo">
                  <div className="text-sm text-gray-900" data-id="element-10409" data-mp="1hce8qgwbo91p">{room.code}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10410" data-mp="12eu4xdb5wjx4">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${room.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-10411" data-mp="12goho4ca55q4">
                    {room.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium relative" data-id="element-10412" data-mp="jom79aq8elob">
                  <button aria-label="Room actions" aria-expanded={actionMenuOpen === room.id} className="text-gray-400 hover:text-gray-500" onClick={() => toggleActionMenu(room.id)} data-id="element-10413" data-mp="omdzy6ypabtk">
                    <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-10414" data-mp="3s5tm29u79pcp" />
                  </button>
                  {actionMenuOpen === room.id && <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-10415" data-mp="3n8l3w991xlbg">
                      <div className="py-1" role="menu" aria-orientation="vertical" data-id="element-10416" data-mp="2amcc5zzly1">
                        <button onClick={() => {
                    setActionMenuOpen(null);
                    navigateToEditRoom(room.id);
                  }} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" data-id="element-10417" data-mp="exgtj2gtxqsy">
                          <EditIcon size={16} className="mr-2" data-id="element-10418" data-mp="xr488dezv85t" />
                          {t('pages.Rooms.edit')}
                        </button>
                        <button onClick={() => {
                    setActionMenuOpen(null);
                    navigateToRoomAssets(room.id);
                  }} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" data-id="element-10419" data-mp="sp55n5r2wf9x">
                          <ImageIcon size={16} className="mr-2" data-id="element-10420" data-mp="rsoo9q2ddxxm" />
                          {t('pages.Rooms.manage_images')}
                        </button>
                        <button onClick={() => {
                    setActionMenuOpen(null);
                    deleteRoom(room.id);
                  }} className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left" data-id="element-10421" data-mp="1kgc3sqos9yde">
                          <TrashIcon size={16} className="mr-2" data-id="element-10422" data-mp="4341sx70cijl" />
                          {t('pages.Rooms.delete')}
                        </button>
                      </div>
                    </div>}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}